import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HashRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Suspense, useEffect } from "react";
import { ROUTE_PATHS } from "@/lib/index";
import Home from "@/pages/Home";
import Properties from "@/pages/Properties";
import Investment from "@/pages/Investment";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProperties from "@/pages/admin/AdminProperties";
import PropertyForm from "@/pages/admin/PropertyForm";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminFiles from "@/pages/admin/AdminFiles";
import AdminSettings from "@/pages/admin/AdminSettings";
import PropertyDetail from "@/pages/PropertyDetail";

const queryClient = new QueryClient();

function ScrollToTop(): null {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname, location.key]);
  return null;
}

const LoadingSpinner = () => (
  <div className="min-h-screen bg-primary flex items-center justify-center">
    <div className="w-16 h-16 border-4 border-accent border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <Router>
        <ScrollToTop />
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path={ROUTE_PATHS.HOME} element={<Home />} />
            <Route path={ROUTE_PATHS.PROPERTIES} element={<Properties />} />
            <Route path={ROUTE_PATHS.INVESTMENT} element={<Investment />} />
            <Route path={ROUTE_PATHS.ABOUT} element={<About />} />
            <Route path={ROUTE_PATHS.CONTACT} element={<Contact />} />
            <Route path={ROUTE_PATHS.PROPERTY_DETAIL} element={<PropertyDetail />} />
            <Route path={ROUTE_PATHS.ADMIN} element={<Navigate to={ROUTE_PATHS.ADMIN_LOGIN} replace />} />
            <Route path={ROUTE_PATHS.ADMIN_LOGIN} element={<AdminLogin />} />
            <Route path={ROUTE_PATHS.ADMIN_DASHBOARD} element={<AdminDashboard />} />
            <Route path={ROUTE_PATHS.ADMIN_PROPERTIES} element={<AdminProperties />} />
            <Route path={ROUTE_PATHS.ADMIN_PROPERTIES_NEW} element={<PropertyForm />} />
            <Route path={ROUTE_PATHS.ADMIN_PROPERTIES_EDIT} element={<PropertyForm />} />
            <Route path={ROUTE_PATHS.ADMIN_CATEGORIES} element={<AdminCategories />} />
            <Route path={ROUTE_PATHS.ADMIN_FILES} element={<AdminFiles />} />
            <Route path={ROUTE_PATHS.ADMIN_SETTINGS} element={<AdminSettings />} />
            <Route path="*" element={<Navigate to={ROUTE_PATHS.HOME} replace />} />
          </Routes>
        </Suspense>
      </Router>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;